import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import config from "../coreFIles/config";
import { getAdminAuthToken } from "../coreFIles/auth";
import CKEditorWithSource from "./CKEditorWithSource";
import {
  adminCmsContentCreateAction,
  adminCmsContentGetAction,
  adminCmsContentUpdateAction
} from "../Action/action";

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function editorPlainText(html) {
  return String(html || "")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildApiUrl(endpoint) {
  const base = (config.apiUrl || "").replace(/\/+$/, "");
  const path = String(endpoint || "").replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

class CkUploadAdapter {
  constructor(loader) {
    this.loader = loader;
    this.controller = new AbortController();
  }

  abort() {
    try {
      this.controller.abort();
    } catch (e) {}
  }

  async upload() {
    const file = await this.loader.file;
    const fd = new FormData();
    fd.append("upload", file);

    const token = getAdminAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
      headers,
      withCredentials: true,
      signal: this.controller.signal
    });

    const url = resp?.data?.url;
    if (!url) throw new Error("Upload failed");

    return { default: resolvePublicUrl(url) };
  }
}

function CkUploadAdapterPlugin(editor) {
  // eslint-disable-next-line no-param-reassign
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new CkUploadAdapter(loader);
}

export default function CmsContentForm({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const isEdit = mode === "edit";

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [contentKey, setContentKey] = useState("");
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isEdit) return;
      setLoading(true);
      setError("");
      try {
        const res = await adminCmsContentGetAction(id);
        if (cancelled) return;
        if (!res?.success) {
          setError(res?.message || res?.msg || "Failed to load CMS content.");
          return;
        }
        const d = res.data || {};
        setContentKey(d.content_key || "");
        setTitle(d.title || "");
        setShortDescription(d.short_description || "");
        setDescription(d.description || "");
        setActive(d.active !== undefined && d.active !== null ? (Number(d.active) ? 1 : 0) : 1);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load CMS content.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id]);

  const validate = () => {
    const errs = {};
    if (!isEdit && !String(contentKey || "").trim()) errs.contentKey = "Content key is required.";
    if (!String(title || "").trim()) errs.title = "Title is required.";
    if (!editorPlainText(shortDescription)) errs.shortDescription = "Short description is required.";
    if (!editorPlainText(description)) errs.description = "Description is required.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSave = async () => {
    if (saving) return;
    setError("");
    if (!validate()) return;

    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          title: String(title || "").trim(),
          short_description: String(shortDescription || "").trim(),
          description: String(description || "").trim(),
          active: active ? 1 : 0
        };
        const res = await adminCmsContentUpdateAction(id, payload);
        if (!res?.success) throw new Error(res?.message || res?.msg || "Update failed");
        toast.success("CMS content updated", { toastId: "cms-content-save" });
      } else {
        const payload = {
          content_key: String(contentKey || "").trim(),
          title: String(title || "").trim(),
          short_description: String(shortDescription || "").trim(),
          description: String(description || "").trim(),
          active: active ? 1 : 0
        };
        const res = await adminCmsContentCreateAction(payload);
        if (!res?.success) throw new Error(res?.message || res?.msg || "Create failed");
        toast.success("CMS content created", { toastId: "cms-content-save" });
      }
      navigate("/cms-content");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Save failed");
      toast.error(e?.response?.data?.message || e?.message || "Save failed", { toastId: "cms-content-save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {(loading || saving) && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
        </div>
      )}

      <div className="toolbar">
        <div className="breadcrumb">
          <Link to="/cms-content">CMS Content</Link>
          <span>›</span>
          <span>{isEdit ? "Edit" : "Add"} CMS Content</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/cms-content" className="btn btn-primary">
            ← Back
          </Link>
        </div>
      </div>

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Save failed.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="settings-section">
          <h3>CMS Content Details</h3>

          <div className="form-row">
            <div className="form-group">
              <label>
                Content Key <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={contentKey}
                disabled={isEdit}
                onChange={(e) => {
                  setContentKey(e.target.value);
                  setFieldErrors((p) => ({ ...(p || {}), contentKey: "" }));
                }}
                placeholder="e.g. ABOUT_US"
                style={fieldErrors.contentKey ? { borderColor: "var(--danger)" } : undefined}
              />
              {isEdit ? (
                <p className="small-text mt-2">Content key cannot be changed after creation.</p>
              ) : (
                <p className="small-text mt-2">Will be stored uppercase without spaces.</p>
              )}
              {fieldErrors.contentKey ? <div className="text-danger field-error">{fieldErrors.contentKey}</div> : null}
            </div>

            <div className="form-group">
              <label>Status</label>
              <select className="form-control" value={active} onChange={(e) => setActive(Number(e.target.value))}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>
              Title <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setFieldErrors((p) => ({ ...(p || {}), title: "" }));
              }}
              placeholder="Content title"
              style={fieldErrors.title ? { borderColor: "var(--danger)" } : undefined}
            />
            {fieldErrors.title ? <div className="text-danger field-error">{fieldErrors.title}</div> : null}
          </div>

          <div className="form-group">
            <label>
              Short Description <span className="text-danger">*</span>
            </label>
            <CKEditorWithSource
              heightClassName="ckeditor-height-200"
              containerStyle={
                fieldErrors.shortDescription
                  ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                  : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
              }
              config={{ extraPlugins: [CkUploadAdapterPlugin] }}
              data={shortDescription}
              onChange={(html) => {
                setShortDescription(html);
                setFieldErrors((p) => ({ ...(p || {}), shortDescription: "" }));
              }}
            />
            {fieldErrors.shortDescription ? (
              <div className="text-danger field-error">{fieldErrors.shortDescription}</div>
            ) : null}
          </div>

          <div className="form-group">
            <label>
              Description <span className="text-danger">*</span>
            </label>
            <CKEditorWithSource
              heightClassName="ckeditor-height-400"
              containerStyle={
                fieldErrors.description
                  ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                  : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
              }
              config={{ extraPlugins: [CkUploadAdapterPlugin] }}
              data={description}
              onChange={(html) => {
                setDescription(html);
                setFieldErrors((p) => ({ ...(p || {}), description: "" }));
              }}
            />
            {fieldErrors.description ? <div className="text-danger field-error">{fieldErrors.description}</div> : null}
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button className="btn btn-primary" type="button" onClick={onSave} disabled={saving || loading}>
          {saving ? (
            "Saving…"
          ) : (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
                <path d="M17 21v-8H7v8" />
                <path d="M7 3v5h8" />
              </svg>
              {isEdit ? "Update CMS Content" : "Save CMS Content"}
            </>
          )}
        </button>
        <Link to="/cms-content" className="btn btn-outline">
          Cancel
        </Link>
      </div>
    </div>
  );
}
